/**
 * Group Filing Dashboard — PAN-wide filing matrix on-screen.
 * Shows every authenticated GSTIN of a PAN × 12 months of the FY as a grid.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2 } from "lucide-react";

const FY_LIST = (() => {
  const d = new Date();
  const startYr = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return Array.from({ length: 6 }, (_, i) => {
    const y = startYr - i;
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  });
})();

const MONTHS = ["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"];

export default function GroupFilingDashboard() {
  const [gstins, setGstins] = useState([]);
  const [pan, setPan] = useState("");
  const [fy, setFy] = useState(FY_LIST[0]);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/company/my-gstins").then(r => {
      const items = r.data.items || [];
      setGstins(items);
      // Guess PAN from default GSTIN
      const def = r.data.default || items[0];
      if (def) setPan(def.gstin.slice(2, 12));
    }).catch(() => {});
  }, []);

  const fetchMatrix = async () => {
    if (!pan || pan.length !== 10) { toast.error("Enter a valid 10-char PAN"); return; }
    setLoading(true); setMatrix(null);
    try {
      const r = await api.get("/gst-reports/filing-dashboard-pan",
        { params: { pan, fy } });
      setMatrix(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fetch failed");
    }
    setLoading(false);
  };

  const startYr = parseInt(fy.split("-")[0], 10);
  const periods = Array.from({length: 12}, (_, i) => {
    const m = ((i + 3) % 12) + 1;
    const y = i < 9 ? startYr : startYr + 1;
    return `${String(m).padStart(2,"0")}${y}`;
  });

  const cellFor = (entries, rt, p) => {
    const e = (entries || []).find(x =>
      String(x.rtntype || x.return_type || "").toUpperCase() === rt &&
      String(x.ret_prd || x.period || "") === p
    );
    return e ? { filed: !!(e.dof || e.filed_dt), dof: e.dof, arn: e.arn } : null;
  };

  const uniquePans = Array.from(new Set(gstins.map(g => g.gstin.slice(2, 12))));

  return (
    <div className="p-6 space-y-6" data-testid="group-filing-dashboard">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <ShieldCheck className="w-8 h-8 text-emerald-500" />
          Group Filing Dashboard
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          PAN-wide filing matrix — every authenticated GSTIN × 12 months of the FY, in one grid.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle>Filters</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label>PAN (10-char)</Label>
            <select value={pan} onChange={(e) => setPan(e.target.value)}
                      className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background font-mono"
                      data-testid="input-pan">
              {uniquePans.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <Label>Financial Year</Label>
            <select value={fy} onChange={(e) => setFy(e.target.value)}
                      className="w-full mt-1 border rounded px-2 py-2 text-sm bg-background"
                      data-testid="input-fy">
              {FY_LIST.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <Button onClick={fetchMatrix} disabled={loading || !pan}
                    data-testid="btn-fetch-matrix">
            {loading ? "Loading…" : "Load Matrix"}
          </Button>
        </CardContent>
      </Card>

      {matrix && (
        <Card>
          <CardHeader>
            <CardTitle>
              PAN {matrix.pan} · FY {matrix.fy} · {matrix.gstins?.length || 0} GSTIN(s)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {["GSTR1", "GSTR3B"].map(rt => (
              <div key={rt} className="mb-6">
                <div className="text-sm font-semibold mb-2">{rt}</div>
                <div className="overflow-x-auto border rounded">
                  <table className="min-w-full text-xs" data-testid={`matrix-${rt}`}>
                    <thead className="bg-primary/10">
                      <tr>
                        <th className="p-2 text-left sticky left-0 bg-primary/10 z-10">GSTIN</th>
                        {MONTHS.map(m => <th key={m} className="p-2 text-center">{m}</th>)}
                        <th className="p-2 text-center">Filed / 12</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(matrix.gstins || []).map(g => {
                        let filed = 0;
                        const cells = periods.map(p => cellFor(g.entries, rt, p));
                        cells.forEach(c => { if (c?.filed) filed++; });
                        return (
                          <tr key={g.gstin} className="border-t hover:bg-muted/20">
                            <td className="p-2 font-mono font-semibold sticky left-0 bg-background">{g.gstin}</td>
                            {cells.map((c, i) => (
                              <td key={i}
                                    title={c?.arn ? `ARN ${c.arn}` : "Not filed"}
                                    className={`p-2 text-center ${
                                      !c ? "text-muted-foreground bg-muted/20" :
                                      c.filed ? "bg-emerald-500/20" : "bg-red-500/10"
                                    }`}>
                                {c?.filed ? "✓" : c ? "—" : "—"}
                                {c?.dof && <div className="text-[9px] text-muted-foreground">{c.dof.slice(0,5)}</div>}
                              </td>
                            ))}
                            <td className="p-2 text-center font-semibold">
                              <span className={filed === 12 ? "text-emerald-600" : "text-amber-600"}>
                                {filed} / 12
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            <div className="text-xs text-muted-foreground mt-2">
              <CheckCircle2 className="inline w-3 h-3 text-emerald-500 mr-1" /> Filed with ARN ·
              <span className="mx-2">— Not filed</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
